import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import { safeFormat } from '../utils/dateUtils';

const Invoices = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    barber_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, barbersRes] = await Promise.all([api.get('/invoices'), api.get('/barbers')]);
      setInvoices(invoicesRes.data);
      setBarbers(barbersRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount), date: new Date(formData.date).toISOString() };
      await api.post('/invoices', payload);
      toast.success('تم إضافة الفاتورة بنجاح');
      setOpen(false);
      setFormData({ barber_id: '', amount: '', date: new Date().toISOString().split('T')[0], description: '', status: 'pending' });
      fetchData();
    } catch (error) {
      toast.error('فشلت العملية');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الفاتورة؟')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('تم حذف الفاتورة بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حذف الفاتورة');
    }
  };

  if (!isAdmin()) {
    return <div className="text-center py-12"><p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p></div>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('invoices')}</h1>
          <p className="text-muted-foreground">إدارة الفواتير</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 me-2" />{t('addInvoice')}</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader><DialogTitle>{t('addInvoice')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">الحلاق (اختياري)</label>
                <Select value={formData.barber_id} onValueChange={(value) => setFormData({ ...formData, barber_id: value })}>
                  <SelectTrigger data-testid="invoice-barber-select" className="bg-secondary/50 border-white/10"><SelectValue placeholder="اختر الحلاق" /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {barbers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2"><CurrencyLabel label={t('amount')} /></label>
                <Input data-testid="invoice-amount-input" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required className="bg-secondary/50 border-white/10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('date')}</label>
                <Input data-testid="invoice-date-input" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="bg-secondary/50 border-white/10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('description')}</label>
                <Input data-testid="invoice-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-secondary/50 border-white/10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('status')}</label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="invoice-status-select" className="bg-secondary/50 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="pending">قيد الانتظار</SelectItem>
                    <SelectItem value="paid">مدفوع</SelectItem>
                    <SelectItem value="cancelled">ملغي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="submit-invoice-btn" type="submit" className="w-full bg-primary text-primary-foreground">{t('save')}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('date')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحلاق</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('amount')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('status')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('description')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const barber = barbers.find(b => b.id === invoice.barber_id);
                return (
                  <tr key={invoice.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">{safeFormat(invoice.date, 'yyyy-MM-dd', { fallback: '-' })}</td>
                    <td className="px-6 py-4">{barber?.name || 'عام'}</td>
                    <td className="px-6 py-4 font-semibold"><CurrencyDisplay amount={invoice.amount} /></td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        invoice.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {invoice.status === 'paid' ? 'مدفوع' : invoice.status === 'pending' ? 'قيد الانتظار' : 'ملغي'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{invoice.description || '-'}</td>
                    <td className="px-6 py-4">
                      <Button data-testid={`delete-invoice-${invoice.id}`} onClick={() => handleDelete(invoice.id)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Invoices;